import { get, post } from './index';
import { Listing, ListingStatus, MockupEntryHydrated } from '../types/listing';
import type { HomepageView, HomeSceneCrop } from '../types/homepage';
import { ProductFormat } from '../types/productFormat';
import { Design } from '../types/design';
import { Collection, CollectionStatus } from '../types/collection';
import type { ListOrdersResponse, OrderDetail } from '../types/order';

const ENDPOINTS = {
  listingsCrud: import.meta.env.VITE_LISTINGS_CRUD_API_ENDPOINT,
  formatsCrud: import.meta.env.VITE_PRODUCT_FORMATS_CRUD_API_ENDPOINT,
  collectionsCrud: import.meta.env.VITE_COLLECTIONS_CRUD_API_ENDPOINT,
  publishCollection: import.meta.env.VITE_PUBLISH_COLLECTION_API_ENDPOINT,
  pricingPreview: import.meta.env.VITE_PRICING_PREVIEW_API_ENDPOINT,
  generateListingMetadata: import.meta.env.VITE_GENERATE_LISTING_METADATA_API_ENDPOINT,
  generateCollectionMetadata: import.meta.env.VITE_GENERATE_COLLECTION_METADATA_API_ENDPOINT,
  publishListing: import.meta.env.VITE_PUBLISH_LISTING_API_ENDPOINT,
  mutateListing: import.meta.env.VITE_MUTATE_LISTING_API_ENDPOINT,
  storefrontSettings: import.meta.env.VITE_STOREFRONT_SETTINGS_API_ENDPOINT,
  fetchRecordsByDesignId: import.meta.env.VITE_FETCH_RECORDS_BY_DESIGN_ID_API_ENDPOINT,
  fetchAllRecords: import.meta.env.VITE_FETCH_ALL_RECORDS_API_ENDPOINT,
  generateMockups: import.meta.env.VITE_GENERATE_MOCKUPS_API_ENDPOINT,
  generateHeroMockup: import.meta.env.VITE_GENERATE_HERO_MOCKUP_API_ENDPOINT,
  listOrders: import.meta.env.VITE_LIST_ORDERS_API_ENDPOINT,
  getOrder: import.meta.env.VITE_GET_ORDER_API_ENDPOINT,
};

// Eager check: surface a missing-deploy gap at module load instead of as a
// confusing network error on first button click. The build-time fetchSSM
// silently omits this line from .env.local if SSM doesn't carry the URL yet,
// so a fresh-clone dev env without backend deployed reaches the wrapper with
// undefined and produces an unhelpful error in the network panel.
if (!ENDPOINTS.generateHeroMockup) {
  throw new Error(
    'Backend deploy required — VITE_GENERATE_HERO_MOCKUP_API_ENDPOINT missing. ' +
      'Run `npm run cdk:deploy -- --env=dev` in the geisti-service repo, then ' +
      '`npm run build -- --env=dev` here.'
  );
}

if (!ENDPOINTS.storefrontSettings) {
  throw new Error(
    'Backend deploy required — VITE_STOREFRONT_SETTINGS_API_ENDPOINT missing. ' +
      'Run `npm run cdk:deploy -- --env=dev` in the geisti-service repo, then ' +
      '`npm run build -- --env=dev` here.'
  );
}

// ----- Listings CRUD -----

// TODO V1.1: add cursor/limit pagination once the catalog grows past
// ~500 listings. Backend's `handleList` does a full Scan today; safe
// at current scale but won't be at 5K.
export const listListings = (params?: { status?: ListingStatus }): Promise<Listing[]> =>
  post(ENDPOINTS.listingsCrud, { action: 'list', ...(params ?? {}) });

export const getListing = (listingId: string): Promise<Listing> =>
  post(ENDPOINTS.listingsCrud, { action: 'get', listingId });

export const createListing = (listing: Partial<Listing>): Promise<{ listingId: string }> =>
  post(ENDPOINTS.listingsCrud, { action: 'create', listing });

// Backend returns only `{ listingId }` — NOT the full updated Listing. Callers
// that need the post-update Listing must merge the diff locally:
//   await updateListing(listingId, diff);
//   setListing(prev => prev ? { ...prev, ...diff } : prev);
export const updateListing = (
  listingId: string,
  diff: Partial<Listing>
): Promise<{ listingId: string }> =>
  post(ENDPOINTS.listingsCrud, { action: 'update', listingId, diff });

export const deleteListing = (listingId: string): Promise<{ listingId: string; deleted: true }> =>
  post(ENDPOINTS.listingsCrud, { action: 'delete', listingId });

// ----- Publish / Mutate -----

export const publishListing = (params: {
  listingId: string;
  idempotencyKey: string;
}): Promise<{
  listingId: string;
  shopifyProductId: string;
  shopifyProductHandle: string;
  publishedAt: string;
}> =>
  post(
    ENDPOINTS.publishListing,
    { listingId: params.listingId },
    { headers: { 'Idempotency-Key': params.idempotencyKey } }
  );

export const mutateListing = (params: {
  listingId: string;
  diff: Record<string, unknown>;
  idempotencyKey: string;
}): Promise<{ listingId: string; shopifyUpdatedAt?: string; appliedFields?: string[] }> =>
  post(
    ENDPOINTS.mutateListing,
    { listingId: params.listingId, diff: params.diff },
    { headers: { 'Idempotency-Key': params.idempotencyKey } }
  );

// ----- Homepage wheel -----
// storefrontSettingsLambda; actions: getHomepage, saveHomepage, generateScene,
// publishHomepage. All return a HomepageView except generateScene, which only
// starts the async render (`{ sceneState: 'generating' }`) — refetch after it.

export const getHomepage = (): Promise<HomepageView> =>
  post(ENDPOINTS.storefrontSettings, { action: 'getHomepage' });

export const saveHomepage = (params: {
  sceneListingIds: string[];
  labels: Record<string, string>;
}): Promise<HomepageView> =>
  post(ENDPOINTS.storefrontSettings, {
    action: 'saveHomepage',
    sceneListingIds: params.sceneListingIds,
    labels: params.labels,
  });

export const generateScene = (params: {
  listingId: string;
  crop?: HomeSceneCrop;
  stalks?: boolean;
  idempotencyKey: string;
}): Promise<{ sceneState: 'generating' }> =>
  post(
    ENDPOINTS.storefrontSettings,
    {
      action: 'generateScene',
      listingId: params.listingId,
      crop: params.crop,
      stalks: params.stalks,
    },
    { headers: { 'Idempotency-Key': params.idempotencyKey } }
  );

export const publishHomepage = (params: { idempotencyKey: string }): Promise<HomepageView> =>
  post(
    ENDPOINTS.storefrontSettings,
    { action: 'publishHomepage' },
    { headers: { 'Idempotency-Key': params.idempotencyKey } }
  );

// ----- Generate Mockups -----

/**
 * Trigger Printify-backed mockup generation for a Listing. Backend mirrors
 * to S3 and writes Listing.mockupArtworkHash + Listing.mockupUrls. Idempotent
 * via the standard Idempotency-Key header.
 *
 * @param params.listingId — the Listing id
 * @param params.idempotencyKey — from useIdempotencyKey(listingId, 'generate-mockups')
 */
export const generateMockups = (params: {
  listingId: string;
  idempotencyKey: string;
}): Promise<{
  mockupUrls: MockupEntryHydrated[];
  mockupArtworkHash: string;
  /** Names the scene plan these mockups were rendered under. */
  renderKey: string;
  /** Per-run render stats; `null` on a cache hit. */
  timings: {
    totalMs: number;
    localScenes: number;
    printifyScenes: number;
    renderMsP50: number;
  } | null;
  cacheHit: boolean;
  durationMs: number;
}> =>
  post(
    ENDPOINTS.generateMockups,
    { listingId: params.listingId },
    { headers: { 'Idempotency-Key': params.idempotencyKey } }
  );

// ----- Generate Hero Mockup -----

/**
 * Trigger Nano-Banana-Pro-backed hero shot generation for a Listing. Backend
 * mirrors the result to S3 at `{listingId}/{currentSampleHash}/hero-{defaultVariantId}.png`
 * and writes hero fields (heroMockupS3Key, heroArtworkHash, heroSourceVariantId,
 * heroMetaPromptVersion) onto the Listing. Cache-first inside the lambda — a hit
 * returns in ~100ms with no model call.
 *
 * @param params.listingId — the Listing id
 * @param params.idempotencyKey — from useIdempotencyKey(listingId, 'generate-hero')
 */
export const generateHeroMockup = (params: {
  listingId: string;
  idempotencyKey: string;
}): Promise<{
  heroMockupS3Key: string;
  heroArtworkHash: string;
  heroSourceVariantId: string;
  heroMetaPromptVersion: string;
  heroMockupSrc: string;
  cacheHit: boolean;
}> =>
  post(
    ENDPOINTS.generateHeroMockup,
    { listingId: params.listingId },
    { headers: { 'Idempotency-Key': params.idempotencyKey } }
  );

// ----- Pricing Preview -----

type PricingPreviewResult = Awaited<ReturnType<typeof requestPricingPreview>>;
// Identical requests in flight share one call: the listing page's MediumsPicker and PricingCard
// both preview every medium on mount, with the same body.
const pricingPreviewInFlight = new Map<string, Promise<PricingPreviewResult>>();

export const pricingPreview = (params: {
  formatId: string;
  variantPriceOverrides?: Record<string, number>;
  costTier?: 'regular' | 'premium';
}): Promise<PricingPreviewResult> => {
  const overrides = params.variantPriceOverrides ?? {};
  const key = JSON.stringify([
    params.formatId,
    params.costTier ?? null,
    Object.keys(overrides)
      .sort()
      .map(id => [id, overrides[id]]),
  ]);
  const inFlight = pricingPreviewInFlight.get(key);
  if (inFlight) return inFlight;
  const request = requestPricingPreview(params).finally(() => pricingPreviewInFlight.delete(key));
  pricingPreviewInFlight.set(key, request);
  return request;
};

const requestPricingPreview = (params: {
  formatId: string;
  variantPriceOverrides?: Record<string, number>;
  costTier?: 'regular' | 'premium';
}): Promise<{
  formatId: string;
  variants: Array<{
    variantId: string;
    autoPriceCents: number;
    finalPriceCents: number;
    breakdown: {
      printifyVariantCostCents: number;
      printifyShippingFirstItemCents: number;
      shopifyProcessingCents: number;
      landedCostCents: number;
    };
  }>;
}> => {
  const body: Record<string, unknown> = { formatId: params.formatId };
  if (params.variantPriceOverrides && Object.keys(params.variantPriceOverrides).length > 0) {
    body.variantPriceOverrides = params.variantPriceOverrides;
  }
  if (params.costTier) {
    body.costTier = params.costTier;
  }
  return post(ENDPOINTS.pricingPreview, body);
};

// ----- Generate listing metadata (Haiku) -----

export interface GenerateListingMetadataResponse {
  displayTitle: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
}

export const generateListingMetadata = (params: {
  promptTemplateText: string;
  mediumNames: string[];
  productType?: string;
}): Promise<GenerateListingMetadataResponse> => post(ENDPOINTS.generateListingMetadata, params);

// ----- Generate collection metadata (Haiku) -----

export interface GenerateCollectionMetadataMemberListing {
  displayTitle: string;
  description: string;
}

export interface GenerateCollectionMetadataResponse {
  displayTitle: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
}

export const generateCollectionMetadata = (params: {
  collectionDisplayTitle: string;
  memberListings: GenerateCollectionMetadataMemberListing[];
}): Promise<GenerateCollectionMetadataResponse> =>
  post(ENDPOINTS.generateCollectionMetadata, params);

// ----- Formats CRUD -----

export const listFormats = (): Promise<ProductFormat[]> =>
  post(ENDPOINTS.formatsCrud, { action: 'list' });

export const getFormat = (formatId: string): Promise<ProductFormat> =>
  post(ENDPOINTS.formatsCrud, { action: 'get', formatId });

export const createFormat = (
  format: Omit<
    ProductFormat,
    | 'variants'
    | 'aspectRatio'
    | 'productType'
    | 'isActive'
    | 'createdAt'
    | 'costFetchedAt'
    | 'mediumLabel'
  > & {
    selectedVariantIds: string[];
    renderConfig: ProductFormat['renderConfig'];
    blueprintImageUrl?: string;
  }
): Promise<{ formatId: string }> => post(ENDPOINTS.formatsCrud, { action: 'create', format });

export const updateFormat = (params: {
  formatId: string;
  /** `productScene: null` resets the Format to its `defaultProductScene`. */
  diff: Omit<Partial<ProductFormat>, 'productScene'> & { productScene?: string | null };
}): Promise<ProductFormat> => post(ENDPOINTS.formatsCrud, { action: 'update', ...params });

/**
 * Refreshes a format's per-variant cost, shipping, and availability from Printify.
 * Hits three live Printify endpoints (one undocumented — see PRINTIFY-UNDOCUMENTED-APIS.md).
 * Throws Error with operator-readable .message on any failure (5xx, shape mismatch, ratio drift).
 * Returns the FULL updated ProductFormat — DO NOT local-merge the response (parallels updateFormat asymmetry).
 * No Idempotency-Key — replay-safe; button isLoading prevents double-click.
 */
export const refreshFormatCosts = (params: { formatId: string }): Promise<ProductFormat> =>
  post(ENDPOINTS.formatsCrud, { action: 'refreshCosts', formatId: params.formatId });

/**
 * Operator "Recalibrate": re-runs preview-scene calibration over every selected
 * variant. The lambda marks the format `mockupTemplatesStatus: 'pending'` and
 * returns 202 immediately — calibration finishes asynchronously (minutes), so
 * refetch the format later to see `ready` / `failed`.
 */
export const calibrateFormatTemplates = async (formatId: string): Promise<void> => {
  await post(ENDPOINTS.formatsCrud, { action: 'calibrateTemplates', formatId });
};

export const softDeleteFormat = (
  formatId: string
): Promise<{
  formatId: string;
  isActive: 'false';
  archivedListings?: Array<{ listingId: string; displayTitle?: string }>;
}> => post(ENDPOINTS.formatsCrud, { action: 'softDelete', formatId });

// ----- Polling -----

export const fetchRecordsByDesignId = (params: { designId: string }): Promise<unknown> =>
  post(ENDPOINTS.fetchRecordsByDesignId, params);

// ----- Designs (design-driven listing flow) -----

export const listDesigns = (params: {
  modelKey: string;
  aspectRatio: string;
  cursor?: string;
}): Promise<{ items: Design[]; nextCursor?: string }> => {
  const base = ENDPOINTS.fetchAllRecords as string;
  const qs = new URLSearchParams({
    modelKey: params.modelKey,
    aspectRatio: params.aspectRatio,
    limit: '20',
  });
  if (params.cursor) qs.set('cursor', params.cursor);
  return get(`${base}designs?${qs.toString()}`);
};

// ----- Orders -----

export const listOrders = (params?: { tab?: 'triage' | 'all' }): Promise<ListOrdersResponse> =>
  post(ENDPOINTS.listOrders as string, params ?? {});

export const getOrder = (shopifyOrderId: string): Promise<OrderDetail> =>
  post(ENDPOINTS.getOrder as string, { shopifyOrderId });

// ----- Collections CRUD -----

export const listCollections = (params?: { status?: CollectionStatus }): Promise<Collection[]> =>
  post(ENDPOINTS.collectionsCrud, { action: 'list', ...(params ?? {}) });

export const getCollection = (collectionId: string): Promise<Collection> =>
  post(ENDPOINTS.collectionsCrud, { action: 'get', collectionId });

export const createCollection = (collection: {
  displayTitle: string;
  description?: string;
  seoTitle?: string;
  seoDescription?: string;
}): Promise<{ collectionId: string }> =>
  post(ENDPOINTS.collectionsCrud, { action: 'create', collection });

// Returns only { collectionId } — caller must local-merge the diff (matches
// updateListing's asymmetry). When the Collection is published, the backend
// also fires Shopify collectionUpdate as a side-effect; we don't echo the
// returned row to avoid an extra read.
export const updateCollection = (
  collectionId: string,
  diff: Partial<
    Pick<Collection, 'displayTitle' | 'description' | 'seoTitle' | 'seoDescription' | 'featured'>
  >
): Promise<{ collectionId: string }> =>
  post(ENDPOINTS.collectionsCrud, { action: 'update', collectionId, diff });

export const softDeleteCollection = (
  collectionId: string
): Promise<{ collectionId: string; status: 'archived' }> =>
  post(ENDPOINTS.collectionsCrud, { action: 'softDelete', collectionId });

// ----- Publish Collection -----

export const publishCollection = (params: {
  collectionId: string;
  idempotencyKey: string;
}): Promise<{ collection: Collection }> =>
  post(
    ENDPOINTS.publishCollection,
    { collectionId: params.collectionId },
    { headers: { 'Idempotency-Key': params.idempotencyKey } }
  );
